/**
 * Thread Add-People — feature flag (group-threading P2c).
 *
 * PLATFORM DEFAULT ON (owner directive 2026-08-17: add-people must work for
 * every resolved account — "nailed out across the platform" — completing the
 * all-accounts rollout approved 2026-07-24 and superseding the earlier
 * sign-off hold). The compile default is consulted AFTER the blank-account
 * guard, so it behaves exactly like a '*' allowlist token: an anonymous /
 * not-yet-resolved session is ALWAYS fail-closed regardless of the default.
 * Rollback: flip this constant to false — the env CSV allowlist below then
 * governs again, unchanged.
 *
 * INDEPENDENT of every other group-threading flag — this flips neither the
 * shared gate nor the panel nor tags.
 */
export const THREAD_ADD_PEOPLE_ENABLED: boolean = true;

/** Build-time env var (comma-separated account ids) naming the enabled accounts. */
export const THREAD_ADD_PEOPLE_ACCOUNTS_ENV = 'REACT_APP_THREAD_ADD_PEOPLE_ACCOUNTS';

export interface AddPeopleEnablementContext {
  accountId?: string | null;
  /** Explicit override — wins over everything (tests / parent-computed). */
  override?: boolean;
  /** Accounts enabled for add-people. Defaults to the build-time env allowlist. */
  enabledAccountIds?: Iterable<string>;
  /**
   * Test seam for the compile default — lets tests exercise the allowlist
   * (rollback) path with `defaultEnabled: false`. Production callers never
   * pass this.
   */
  defaultEnabled?: boolean;
}

/**
 * Parse the account allowlist from a build-time env map. CRA/webpack only inlines a
 * DIRECT static `process.env.REACT_APP_*` member reference into the browser bundle,
 * so read the specific var by direct reference at runtime (tests inject `env`).
 */
export function readEnabledAddPeopleAccountsFromEnv(
  env?: Record<string, string | undefined>,
): string[] {
  const raw =
    (env
      ? env[THREAD_ADD_PEOPLE_ACCOUNTS_ENV]
      : process.env.REACT_APP_THREAD_ADD_PEOPLE_ACCOUNTS) ?? '';
  const seen = new Set<string>();
  for (const part of String(raw).split(',')) {
    const id = part.trim();
    if (id) seen.add(id);
  }
  return [...seen];
}

/**
 * Account-scoped enablement resolver. Precedence: explicit override →
 * blank-account fail-closed guard → (ON) compile default → env CSV allowlist.
 * The blank guard sits ABOVE the compile default on purpose: no default, token,
 * or wildcard ever enables the surface for an unresolved session.
 */
export function resolveThreadAddPeopleEnabled(
  ctx: AddPeopleEnablementContext = {},
): boolean {
  if (ctx.override !== undefined) return ctx.override;

  const account = (ctx.accountId ?? '').trim();
  if (!account) return false;

  if (ctx.defaultEnabled ?? THREAD_ADD_PEOPLE_ENABLED) return true;

  const source = ctx.enabledAccountIds ?? readEnabledAddPeopleAccountsFromEnv();
  for (const raw of source) {
    const token = String(raw ?? '').trim();
    // ALL-ACCOUNTS ROLLOUT (2026-07-24): the literal '*' admits every RESOLVED
    // account — checked after the blank-account guard, so anonymous /
    // not-yet-resolved sessions stay fail-closed. Rollback = repo-var edit.
    if (token === '*' || token === account) return true;
  }
  return false;
}
